/**
 * 意见反馈 (Admin) — 列表、筛选、详情、处理备注
 */

import { authFetch } from './api.js';
import { setFeedbackCurrentPage, getFeedbackCurrentPage } from './state.js';
import { showToast, renderPagination } from './utils.js';
import { registerViewLoader, refreshOpsNavBadge } from './navigation.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';

const STEP_NAMES = {
  1: 'ASIN 抓数据', 2: '构建关键词库', 3: '生成 Listing',
  4: '合规检测', 5: 'Listing 打分', 6: '修改建议', 7: '图片生成',
};
const TYPE_LABELS = { bug: '缺陷', suggestion: '建议', other: '其他' };
const TYPE_CLASSES = { bug: 'feedback-type-bug', suggestion: 'feedback-type-suggestion', other: 'feedback-type-other' };
const FEEDBACK_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  status: { type: 'string', defaultValue: '' },
  type: { type: 'string', defaultValue: '' },
  search: { type: 'string', defaultValue: '' },
};

export function initFeedback() {
  hydrateFeedbackFiltersFromUrl();
  registerViewLoader('admin-feedback', () => {
    loadOpsSummary();
    loadAdminFeedbacks();
  });

  document.getElementById('feedback-status-filter')?.addEventListener('change', () => loadAdminFeedbacks(1));
  document.getElementById('feedback-type-filter')?.addEventListener('change', () => loadAdminFeedbacks(1));
  document.getElementById('feedback-search-btn')?.addEventListener('click', () => loadAdminFeedbacks(1));
  document.getElementById('feedback-search')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') loadAdminFeedbacks(1);
  });
}

function hydrateFeedbackFiltersFromUrl() {
  const state = readScopedQuery('feedback', FEEDBACK_QUERY_SCHEMA);
  const status = document.getElementById('feedback-status-filter');
  const type = document.getElementById('feedback-type-filter');
  const search = document.getElementById('feedback-search');
  if (status) status.value = state.status || '';
  if (type) type.value = state.type || '';
  if (search && state.search) search.value = state.search;
}

function createFeedbackElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createFeedbackHeader() {
  const row = document.createElement('tr');
  ['ID', '类型', '提交者', '业务路径', '描述', '状态', '时间', '操作'].forEach((label) => {
    row.appendChild(createFeedbackElement('th', '', label));
  });
  return row;
}

function createFeedbackSummaryCard(label, value, valueClass = '') {
  const card = createFeedbackElement('div', 'stat-card');
  card.append(
    createFeedbackElement('div', 'stat-card-label', label),
    createFeedbackElement('div', `stat-card-value ${valueClass}`.trim(), value),
  );
  return card;
}

function createFeedbackPath(appState = {}) {
  const stepId = appState.stepId;
  if (stepId) {
    const path = createFeedbackElement('span', 'feedback-path-step', `[${stepId}] ${STEP_NAMES[stepId] || `Step ${stepId}`}`);
    if (appState.sectionName) {
      path.append(document.createTextNode(' → '), createFeedbackElement('code', 'feedback-path-section', appState.sectionName));
    }
    return path;
  }
  if (appState.adminView) return createFeedbackElement('span', 'feedback-path-admin', `管理端 · ${appState.adminView}`);
  return createFeedbackElement('span', 'feedback-path-muted', '-');
}

function createFeedbackRow(item) {
  const row = document.createElement('tr');
  const typeKey = TYPE_LABELS[item.feedback_type] ? item.feedback_type : 'other';
  const description = item.description
    ? `${item.screenshot ? '📎 ' : ''}${item.description.slice(0, 60)}${item.description.length > 60 ? '…' : ''}`
    : '-';
  const status = item.status === 'fixed' ? 'fixed' : 'pending';
  const appState = item.app_state || {};
  row.appendChild(createFeedbackElement('td', '', item.id));
  row.appendChild(createFeedbackElement('td', `feedback-type ${TYPE_CLASSES[typeKey]}`, TYPE_LABELS[typeKey]));
  row.appendChild(createFeedbackElement('td', '', item.display_name || '-'));
  const pathCell = createFeedbackElement('td', 'feedback-path-cell');
  pathCell.appendChild(createFeedbackPath(appState));
  row.appendChild(pathCell);

  const descriptionCell = createFeedbackElement('td', 'feedback-description-cell', description);
  descriptionCell.dataset.action = 'show-feedback-detail';
  descriptionCell.dataset.id = String(item.id ?? '');
  row.appendChild(descriptionCell);
  row.appendChild(createFeedbackElement('td', `feedback-status feedback-status-${status}`, status === 'fixed' ? '✅ 已修复' : '⏳ 待处理'));
  row.appendChild(createFeedbackElement('td', 'feedback-time-cell', new Date(item.created_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })));

  const actionCell = document.createElement('td');
  const action = status === 'pending' ? 'mark-feedback-fixed' : 'reopen-feedback';
  const button = createFeedbackElement('button', status === 'pending' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm', status === 'pending' ? '标记已修复' : '重新打开');
  button.type = 'button';
  button.dataset.action = action;
  button.dataset.id = String(item.id ?? '');
  actionCell.appendChild(button);
  row.appendChild(actionCell);
  return row;
}

function createFeedbackEmptyRow() {
  const cell = createFeedbackElement('td', 'feedback-empty-cell', '暂无反馈');
  cell.colSpan = 8;
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

async function loadOpsSummary() {
  try {
    const resp = await authFetch('/api/admin/ops-summary');
    if (!resp.ok) return;
    const data = await resp.json();
    const el = document.getElementById('feedback-stats-grid');
    if (!el || !data?.feedback) return;
    const f = data.feedback;
    el.replaceChildren(
      createFeedbackSummaryCard('待处理', f.pending, 'feedback-summary-pending'),
      createFeedbackSummaryCard('已修复', f.fixed, 'feedback-summary-fixed'),
      createFeedbackSummaryCard('今日新增', f.today),
    );
  } catch { /* ignore */ }
}

export async function loadAdminFeedbacks(page = null) {
  const queryState = readScopedQuery('feedback', FEEDBACK_QUERY_SCHEMA);
  const targetPage = page == null ? queryState.page : page;
  setFeedbackCurrentPage(targetPage);
  const status = document.getElementById('feedback-status-filter')?.value || '';
  const type = document.getElementById('feedback-type-filter')?.value || '';
  const search = document.getElementById('feedback-search')?.value?.trim() || '';
  writeScopedQuery('feedback', { page: targetPage, status, type, search });
  const params = new URLSearchParams({ page: targetPage, size: 20 });
  if (status) params.set('status', status);
  if (type) params.set('type', type);
  if (search) params.set('search', search);

  const tbody = document.getElementById('feedback-tbody');
  const thead = document.getElementById('feedback-thead');
  if (thead) {
    thead.replaceChildren(createFeedbackHeader());
  }
  tbody?.setAttribute('aria-busy', 'true');
  const data = await withTableLoading({
    tbody,
    colSpan: 8,
    page: targetPage,
    loadingMessage: '正在加载反馈与操作按钮…',
    errorMessage: '加载反馈失败',
    onRetry: (p) => loadAdminFeedbacks(p),
    onError: (err) => {
      console.error('[Feedback]', err);
      showToast('加载反馈数据失败', 'error');
    },
    task: async () => {
    const resp = await authFetch(`/api/admin/feedbacks?${params}`);
    if (!resp.ok) throw new Error('加载失败');
      return resp.json();
    },
  });
  tbody?.removeAttribute('aria-busy');
  if (!data) return;

    if (!data.rows?.length) {
      tbody.replaceChildren(createFeedbackEmptyRow());
      document.getElementById('feedback-pagination')?.replaceChildren();
      return;
    }

    tbody.replaceChildren(...data.rows.map((row) => createFeedbackRow(row)));

    renderPagination('feedback-pagination', data.total, data.page, data.size, (p) => loadAdminFeedbacks(p));
}

export async function markFeedbackFixed(id) {
  const note = prompt('处理备注（可选）：') ?? '';
  try {
    const resp = await authFetch(`/api/admin/feedbacks/${id}`, {
      method: 'PUT', body: JSON.stringify({ status: 'fixed', adminNote: note }),
    });
    if (!resp.ok) throw new Error();
    showToast('已标记为已修复', 'success');
    loadOpsSummary();
    refreshOpsNavBadge(authFetch);
    loadAdminFeedbacks(getFeedbackCurrentPage());
  } catch { showToast('操作失败', 'error'); }
}

export async function reopenFeedback(id) {
  try {
    const resp = await authFetch(`/api/admin/feedbacks/${id}`, {
      method: 'PUT', body: JSON.stringify({ status: 'pending' }),
    });
    if (!resp.ok) throw new Error();
    showToast('已重新打开', 'success');
    loadOpsSummary();
    refreshOpsNavBadge(authFetch);
    loadAdminFeedbacks(getFeedbackCurrentPage());
  } catch { showToast('操作失败', 'error'); }
}

export async function showFeedbackDetail(idOrRow) {
  let row = idOrRow;
  if (typeof idOrRow === 'number' || (typeof idOrRow === 'string' && /^\d+$/.test(String(idOrRow)))) {
    try {
      const resp = await authFetch(`/api/admin/feedbacks/${idOrRow}`);
      if (!resp.ok) throw new Error();
      row = await resp.json();
    } catch { showToast('加载详情失败', 'error'); return; }
  }
  const appState = row.app_state || {};
  const stepId = appState.stepId;
  const sectionName = appState.sectionName || null;
  const stepLabel = stepId ? (STEP_NAMES[stepId] || `Step ${stepId}`) : (appState.adminView ? `管理端 ${appState.adminView}` : '未知');
  let detail = `类型: ${TYPE_LABELS[row.feedback_type] || 'bug'}\n状态: ${row.status === 'fixed' ? '已修复' : '待处理'}\n`;
  if (row.page_url) detail += `页面: ${row.page_url}\n`;
  if (row.admin_note) detail += `处理备注: ${row.admin_note}\n\n`;
  if (stepId || sectionName || appState.adminView) {
    detail += '━━━ 业务路径 ━━━\n';
    if (stepId) detail += `步骤: [${stepId}] ${STEP_NAMES[stepId] || stepId}\n`;
    if (sectionName) detail += `模块: ${sectionName}\n`;
    if (appState.adminView) detail += `管理视图: ${appState.adminView}\n`;
    detail += '\n';
  }
  if (appState.recentErrors?.length) {
    detail += '━━━ 页面近期错误 ━━━\n';
    appState.recentErrors.forEach((e, i) => { detail += `${i + 1}. [${e.time || ''}] ${e.message || e}\n`; });
    detail += '\n';
  }
  if (Array.isArray(row.client_logs) && row.client_logs.length) {
    detail += '━━━ 最近 50 条页面操作 / 报错日志 ━━━\n';
    row.client_logs.slice(-50).forEach((entry, index) => {
      const type = entry?.type === 'request_error' ? '请求失败' : (entry?.type === 'error' ? '错误' : '操作');
      const action = entry?.action || '-';
      const target = entry?.target ? ` · ${entry.target}` : '';
      const message = entry?.message ? ` · ${entry.message}` : '';
      detail += `${index + 1}. [${entry?.time || ''}] [${type}] ${action}${target}${message}\n`;
      if (entry?.type === 'request_error' || entry?.method || entry?.url) {
        detail += `   接口: ${entry?.method || 'GET'} ${entry?.url || entry?.target || '-'}\n`;
        detail += `   状态: ${entry?.status ?? '-'} · 耗时: ${entry?.durationMs ?? '-'}ms`;
        if (entry?.traceId) detail += ` · Trace: ${entry.traceId}`;
        detail += '\n';
        if (entry?.requestSummary) detail += `   入参摘要: ${JSON.stringify(entry.requestSummary, null, 2).replace(/\n/g, '\n   ')}\n`;
      }
    });
    detail += '\n';
  }
  if (Array.isArray(row.server_logs) && row.server_logs.length) {
    detail += '━━━ 提交时的服务端操作 / 错误证据（最近 50 条） ━━━\n';
    row.server_logs.slice(0, 50).forEach((entry, index) => {
      const source = entry?.source || '-';
      const module = entry?.module ? ` · ${entry.module}` : '';
      const status = entry?.status != null ? ` · ${entry.status}` : '';
      const message = entry?.message ? ` · ${entry.message}` : '';
      const matched = entry?.matchedTrace ? ' [精确关联]' : '';
      detail += `${index + 1}. [${entry?.time || ''}] [${entry?.type || 'log'}]${matched} ${source}${module}${status}${message}\n`;
      if (entry?.method) detail += `   方法: ${entry.method}\n`;
      if (entry?.traceId) detail += `   Trace: ${entry.traceId}\n`;
      if (entry?.target) detail += `   目标: ${entry.target}\n`;
      if (entry?.context) detail += `   上下文: ${JSON.stringify(entry.context, null, 2).replace(/\n/g, '\n   ')}\n`;
      if (entry?.detail) detail += `   操作详情: ${JSON.stringify(entry.detail, null, 2).replace(/\n/g, '\n   ')}\n`;
      if (entry?.stack) detail += `   堆栈:\n   ${String(entry.stack).replace(/\n/g, '\n   ')}\n`;
    });
    detail += '\n';
  }
  const rest = { ...appState };
  ['stepId', 'stepName', 'sectionName', 'adminView', 'recentErrors'].forEach((k) => delete rest[k]);
  if (Object.keys(rest).length) detail += '━━━ 应用状态 ━━━\n' + JSON.stringify(rest, null, 2) + '\n\n';
  if (row.browser_info) detail += '━━━ 浏览器 ━━━\n' + JSON.stringify(row.browser_info, null, 2) + '\n\n';
  if (row.description) detail += '━━━ 用户描述 ━━━\n' + row.description;
  document.getElementById('detail-modal-title').textContent = `反馈 #${row.id} — ${stepLabel}`;
  const content = document.getElementById('detail-modal-content');
  content.textContent = detail;
  if (row.screenshot?.url) {
    const snapshot = document.createElement('section');
    snapshot.className = 'feedback-detail-snapshot';
    const title = document.createElement('h4');
    title.textContent = '页面快照附件';
    const image = document.createElement('img');
    image.src = row.screenshot.url;
    image.alt = `反馈 #${row.id} 页面快照`;
    image.className = 'feedback-detail-snapshot-image';
    snapshot.append(title, image);
    content.appendChild(snapshot);
  }
  document.getElementById('detail-modal').hidden = false;
}
